import api from "@/config/api";
import { handleApiError } from "@/utils/ErrorHandler";
import { createPaginationModule, defaultPagination } from "@/store/common/pagination";

const skillsPag = createPaginationModule('SKILLS');

const state = {
  skills: [],
  summarySkills: [], // ✅ summary state
  skillsFromManagedLocation: [], // ✅ summary state
  loading: false,
  error: null,
  pagination: defaultPagination(), // ✅ shared pagination state
  lastSearch: "", // ✅ keep search term for reuse
};

const mutations = {
  ...skillsPag.mutations, // ✅ module-specific pagination mutations

  SET_SKILLS(state, skills) {
    state.skills = skills;
  },
  SET_SUMMARY_SKILLS(state, skills) {
    state.summarySkills = skills;
  },
  SET_SKILLS_FROM_MANAGED_LOCATION(state, skills) {
    state.skillsFromManagedLocation = skills;
  },
  ADD_SKILL(state, skill) {
    state.skills.push(skill);
  },
  REMOVE_SKILL(state, skillId) {
    state.skills = state.skills.filter((s) => s.id !== skillId);
  },
  SET_LOADING(state, val) {
    state.loading = val;
  },
  SET_ERROR(state, err) {
    state.error = err;
  },
};

const actions = {
  /**
   * ✅ Fetch skills by location with pagination and search support
   * API: /api/skills/location/{locationId}?search=&page_number=&page_size=
   */
  async getSkillsByLocation(
    { commit },
    { locationId, search = "", page = 1, pageSize = 10 }
  ) {
    commit("SET_LOADING", true);
    commit("SET_ERROR", null);

    try {
      const response = await api.get(
        `/skills/location/${locationId}?search=${encodeURIComponent(
          search
        )}&page_number=${page}&page_size=${pageSize}`
      );

      const output = response.data.data.output;
      const pagination = response.data.data.pagination;

      const mappedSkills = output.map((item) => ({
        id: item.skillid,
        skillRole: item.skillname,
        description: item.description,
        default: item.is_default ? "Yes" : "No",
        autoAdd: item.interest_auto_add ? "Yes" : "No",
        publicView: item.publicview ? "Yes" : "No",
        preSelect: item.preselect ? "Yes" : "No",
        locationId: item.locationid,
        volunteerCount: item.volunteer_count,
      }));

      commit("SET_SKILLS", mappedSkills);

      // ✅ update module-specific pagination state
      commit(skillsPag.SET_PAGINATION, {
        currentPage: pagination.current_page,
        totalPages: pagination.total_pages,
        totalRecords: pagination.total_records,
        pageSize: pagination.page_size,
        hasPrevious: pagination.has_previous,
        hasNext: pagination.has_next,
      });

      return mappedSkills;
    } catch (error) {
      const message = handleApiError(error, "Failed to fetch skills");
      commit("SET_ERROR", message);
      throw new Error(message);
    } finally {
      commit("SET_LOADING", false);
    }
  },

  /**
   * ✅ Handle page change without resetting search term (for location)
   */
  async changePage({ state, dispatch }, { locationId, page, pageSize }) {
    const currentSearch = state.lastSearch || "";
    await dispatch("getSkillsByLocation", {
      locationId,
      search: currentSearch,
      page,
      pageSize,
    });
  },

  /**
   * ✅ Store search term to persist when changing pages (for location)
   */
  async updateSearchTerm({ dispatch, state }, { locationId, search }) {
    state.lastSearch = search;
    await dispatch("getSkillsByLocation", {
      locationId,
      search,
      page: 1,
      pageSize: state.pagination.pageSize,
    });
  },

  /**
   * ✅ Fetch skills summary with pagination and search support
   * API: /api/skills/summary?locationid=2&max=10&offset=1&search=
   */
  async getSkillsSummary(
    { commit },
    { locationId, search = "", pageNumber = 1, pageSize = 10 }
  ) {
    commit("SET_LOADING", true);
    commit("SET_ERROR", null);

    try {
      const response = await api.get(
        `/skills/summary?locationid=${locationId}&page_number=${pageNumber}&page_size=${pageSize}&search=${encodeURIComponent(
          search
        )}`
      );

      const data = response.data.data;

      const mappedSkills = data.skills.map((skill) => ({
        id: skill.id,
        name: skill.name,
        description: skill.description,
        status: "Active", // API doesn’t return status, defaulting for now
        volunteers: skill.volunteers.map((v) => ({
          id: v.id,
          name: v.full_name,
          email: v.email,
          initials: v.full_name
            .split(" ")
            .map((n) => n[0])
            .join("")
            .toUpperCase(),
          avatarColor: "#3b82f6",
          statusColor: "#10b981",
        })),
        activities: skill.workgroups.map((wg) => ({
          id: wg.activity.id,
          name: wg.display_name,
          frequency: "N/A",
          assigned: skill.volunteers.length,
          type: "Primary",
          borderColor: "#3b82f6",
          badgeColor: "#dbeafe",
          badgeTextColor: "#2563eb",
        })),
        interestedUsers: (skill.interested_users || []).map((u) => ({
          id: u.id,
          name: u.full_name,
          email: u.email,
          initials: u.full_name
            .split(" ")
            .map((n) => n[0])
            .join("")
            .toUpperCase(),
          avatarColor: "#f59e0b",
        })),
      }));

      commit("SET_SUMMARY_SKILLS", mappedSkills);

      // ✅ update pagination info (if backend provides)
      if (data.pagination) {
        commit(skillsPag.SET_PAGINATION, {
          currentPage: data.pagination.current_page,
          totalPages: data.pagination.total_pages,
          totalRecords: data.pagination.total_records,
          pageSize: data.pagination.page_size,
          hasPrevious: data.pagination.has_previous,
          hasNext: data.pagination.has_next,
        });
      }
      return mappedSkills;
    } catch (error) {
      const message = handleApiError(error, "Failed to fetch skills summary");
      commit("SET_ERROR", message);
      throw new Error(message);
    } finally {
      commit("SET_LOADING", false);
    }
  },

  /**
   * ✅ Handle page change (for summary)
   */
  async changeSummaryPage({ state, dispatch }, { locationId, offset, max }) {
    const currentSearch = state.lastSearch || "";
    await dispatch("getSkillsSummary", {
      locationId,
      search: currentSearch,
      offset,
      max,
    });
  },

  /**
   * ✅ Update search term (for summary)
   */
  async updateSummarySearch({ dispatch, state }, { locationId, search }) {
    state.lastSearch = search;
    await dispatch("getSkillsSummary", {
      locationId,
      search,
      offset: 1,
      max: state.pagination.pageSize,
    });
  },

  /**
   * ✅ Create a new skill
   */
  async createSkill({ commit }, skillData) {
    commit("SET_LOADING", true);
    commit("SET_ERROR", null);

    try {
      const response = await api.post("/skills", skillData);
      commit("ADD_SKILL", response.data);
      return response.data;
    } catch (error) {
      const message = handleApiError(error, "Failed to create skill");
      commit("SET_ERROR", message);
      throw new Error(message);
    } finally {
      commit("SET_LOADING", false);
    }
  },

  /**
   * ✅ Delete skill
   */
  async deleteSkill({ commit }, skillId) {
    commit("SET_LOADING", true);
    commit("SET_ERROR", null);

    try {
      await api.delete(`/skills/${skillId}`);
      commit("REMOVE_SKILL", skillId);
      return true;
    } catch (error) {
      const message = handleApiError(error, "Failed to delete skill");
      commit("SET_ERROR", message);
      throw new Error(message);
    } finally {
      commit("SET_LOADING", false);
    }
  },

  async fetchSkillSummary(context, skillId) {
    try {
      const response = await api.get(`/skills/${skillId}/summary`);
      return response.data?.output?.summary_list || [];
    } catch (e) {
      console.log('Error fetching skill summary', e);
      throw e;
    }
  },

  async getSkillsFromManageLocation({ commit }, { locationId }) {
    commit("SET_LOADING", true);
    commit("SET_ERROR", null);

    try {
      const response = await api.post("/managelocationajax", {
        cmd: "getskills",
        locationid: locationId,
      });

      const data = response.data?.output || [];
      commit("SET_SKILLS_FROM_MANAGED_LOCATION", data);
      return data;
    } catch (error) {
      const message = handleApiError(error, "Failed to fetch location skills");
      commit("SET_ERROR", message);
      throw new Error(message);
    } finally {
      commit("SET_LOADING", false);
    }
  },
};

const getters = {
  allSkills: (state) => state.skills,
  summarySkills: (state) => state.summarySkills,
  skillsFromManagedLocation: (state) => state.skillsFromManagedLocation,
  skillsLoading: (state) => state.loading,
  skillsError: (state) => state.error,
  pagination: (state) => state.pagination,
};

export default {
  state,
  mutations,
  actions,
  getters,
};
